import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Checkbox,
  IconButton,
  Divider,
  Stack,
  Tooltip,
} from '@mui/material';
import { Filter as FilterIcon, X, MessageSquare } from 'lucide-mui';
import { RootState } from '../../../../../reducers';
import { getLetterAvatar } from '../../../../../lib/commonUtils';
import {
  toggleSpeakerSelection,
  setShowNotesOnly,
} from '../../../selectedVideoSlice';

function formatSpeakerName(name: string) {
  const match = name.match(/^SPEAKER_(\d+)$/);
  if (match) {
    const speakerNumber = parseInt(match[1], 10) + 1;
    return `Speaker ${speakerNumber}`;
  }
  return name;
}

const Filter = () => {
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { file } = selectedVideo;
  const { speakers = {} } = file?.textData || {};
  const { selectedSpeakerIds, showNotesOnly } = selectedVideo.transcript || {};
  const { compactSegments = [] } = selectedVideo.transcript || {};
  const notes = selectedVideo.file?.textData?.notes || [];
  const dispatch = useDispatch();

  const [speakerAnchorEl, setSpeakerAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const speakerMenuOpen = Boolean(speakerAnchorEl);

  // Get all unique speaker IDs from both sources
  const allSpeakers = useMemo(() => {
    const speakerMap = { ...speakers };

    // Add any speakers from segments that aren't in the speakers map
    compactSegments.forEach((segment) => {
      if (segment.speaker && !speakerMap[segment.speaker]) {
        speakerMap[segment.speaker] = {
          name: segment.speaker,
          picture: '',
          userId: '',
          email: '',
        };
      }
    });

    return speakerMap;
  }, [speakers, compactSegments]);

  const handleSpeakerClick = (event: React.MouseEvent<HTMLElement>) => {
    setSpeakerAnchorEl(event.currentTarget);
  };

  const handleSpeakerClose = () => {
    setSpeakerAnchorEl(null);
  };

  const handleSpeakerSelection = (speakerId: string) => {
    dispatch(toggleSpeakerSelection(speakerId));
  };

  const handleClearSpeakers = () => {
    selectedSpeakerIds.forEach((speakerId) => {
      dispatch(toggleSpeakerSelection(speakerId));
    });
  };

  const handleToggleNotes = () => {
    dispatch(setShowNotesOnly(!showNotesOnly));
  };

  const selectedCount = selectedSpeakerIds.length;
  const totalSpeakers = Object.keys(allSpeakers).length;
  const totalNotes = notes.length;

  const getNotesButtonColor = () => {
    if (totalNotes === 0) return 'text.disabled';
    if (showNotesOnly) return 'primary.main';
    return 'text.secondary';
  };

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'flex-end',
      }}
    >
      <Stack direction="row" spacing={1}>
        <Button
          id="speaker-filter-button"
          aria-controls={speakerMenuOpen ? 'speaker-filter-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={speakerMenuOpen ? 'true' : undefined}
          onClick={handleSpeakerClick}
          startIcon={<FilterIcon />}
          endIcon={
            selectedCount > 0 && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearSpeakers();
                }}
                size="small"
                sx={{
                  p: 0.2,
                  ml: -0.5,
                  mr: -1,
                  color: 'inherit',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <X sx={{ fontSize: 16 }} />
              </IconButton>
            )
          }
          variant="outlined"
          size="small"
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            borderColor: selectedCount > 0 ? 'primary.main' : 'divider',
            backgroundColor: selectedCount > 0 ? 'primary.50' : 'transparent',
            color: selectedCount > 0 ? 'primary.main' : 'text.secondary',
            height: 32,
            px: 1.5,
            '&:hover': {
              borderColor: selectedCount > 0 ? 'primary.main' : 'text.primary',
              backgroundColor:
                selectedCount > 0 ? 'primary.100' : 'action.hover',
            },
          }}
        >
          {selectedCount > 0
            ? `${selectedCount} of ${totalSpeakers} selected`
            : `Filter by speaker (${totalSpeakers})`}
        </Button>
        <Button
          onClick={handleToggleNotes}
          startIcon={<MessageSquare sx={{ fontSize: 18 }} />}
          variant="outlined"
          size="small"
          disabled={totalNotes === 0}
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            borderColor: showNotesOnly ? 'primary.main' : 'divider',
            backgroundColor: showNotesOnly ? 'primary.50' : 'transparent',
            color: getNotesButtonColor(),
            height: 32,
            px: 1.5,
            '&:hover': {
              borderColor: showNotesOnly ? 'primary.main' : 'text.primary',
              backgroundColor: showNotesOnly ? 'primary.100' : 'action.hover',
            },
            '&.Mui-disabled': {
              borderColor: 'divider',
              backgroundColor: 'transparent',
            },
          }}
        >
          {totalNotes === 0 ? (
            <Tooltip
              title="Add notes to segments by clicking the comment icon next to any segment"
              arrow
              placement="top"
            >
              <span>No notes</span>
            </Tooltip>
          ) : (
            `Notes (${totalNotes})`
          )}
        </Button>
      </Stack>

      {/* Speaker Filter Menu */}
      <Menu
        id="speaker-filter-menu"
        anchorEl={speakerAnchorEl}
        open={speakerMenuOpen}
        onClose={handleSpeakerClose}
        MenuListProps={{
          'aria-labelledby': 'speaker-filter-button',
          dense: true,
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 280,
            maxHeight: '70vh',
            borderRadius: 2,
            overflow: 'hidden',
            '& .MuiList-root': {
              padding: 1,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Select Speakers
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedCount > 0
              ? `${selectedCount} of ${totalSpeakers} speakers selected`
              : `${totalSpeakers} speakers available`}
          </Typography>
        </Box>
        <Divider />
        {Object.entries(allSpeakers).map(([speakerId, speaker]) => {
          const isSelected = selectedSpeakerIds.includes(speakerId);
          const displayName = formatSpeakerName(speaker.name);
          return (
            <MenuItem
              key={speakerId}
              onClick={() => handleSpeakerSelection(speakerId)}
              sx={{
                borderRadius: 1,
                mx: 0.5,
                py: 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={isSelected}
                  disableRipple
                  size="small"
                  sx={{
                    color: 'action.active',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              </ListItemIcon>
              <Avatar
                alt={displayName}
                src={speaker.picture}
                sx={{
                  width: 24,
                  height: 24,
                  mr: 1,
                  fontSize: '0.75rem',
                  bgcolor: 'action.selected',
                  color: 'common.white',
                }}
              >
                {getLetterAvatar(displayName)}
              </Avatar>
              <Typography variant="body2">{displayName}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default Filter;
